const VIDEO_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;

/** @param {string} id */
export function isValidYoutubeVideoId(id) {
  return VIDEO_ID_PATTERN.test(String(id || "").trim());
}

/** @param {string} input */
export function parseYoutubeVideoId(input) {
  const value = String(input || "").trim();
  if (!value) return "";

  if (isValidYoutubeVideoId(value)) {
    return value;
  }

  try {
    const url = new URL(value);
    const host = url.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      const id = url.pathname.slice(1).split("/")[0];
      return isValidYoutubeVideoId(id) ? id : "";
    }

    if (host === "youtube.com" || host === "m.youtube.com" || host === "music.youtube.com") {
      if (url.pathname === "/watch" || url.pathname.startsWith("/watch/")) {
        const id = url.searchParams.get("v");
        return isValidYoutubeVideoId(id) ? id : "";
      }

      const parts = url.pathname.split("/").filter(Boolean);
      if (parts[0] === "embed" || parts[0] === "shorts" || parts[0] === "live") {
        const id = parts[1];
        return isValidYoutubeVideoId(id) ? id : "";
      }
    }
  } catch {
    // Not a URL — fall through.
  }

  return "";
}

/** @param {string} videoId */
export function youtubeWatchUrl(videoId) {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

/** @param {string} videoId */
export function youtubeEmbedUrl(videoId) {
  return `https://www.youtube-nocookie.com/embed/${videoId}`;
}

/** @param {string} videoId @param {"maxresdefault" | "sddefault" | "hqdefault"} [variant] */
export function youtubeThumbnailUrl(videoId, variant = "maxresdefault") {
  return `https://i.ytimg.com/vi/${videoId}/${variant}.jpg`;
}

/** Prefer the highest-resolution YouTube still; falls back to upgrading stored low-res URLs. */
export function bestYoutubeThumbnailUrl(videoId, storedUrl) {
  const id = String(videoId || "").trim();
  if (isValidYoutubeVideoId(id)) {
    return youtubeThumbnailUrl(id, "maxresdefault");
  }

  const url = String(storedUrl || "").trim();
  if (url.includes("i.ytimg.com/vi/")) {
    return url.replace(/\/(default|mqdefault|hqdefault|sddefault|maxresdefault)\.jpg/, "/maxresdefault.jpg");
  }

  return url;
}

/** @param {string} src @param {string} [videoId] */
export function youtubeThumbnailFallbackSrc(src, videoId) {
  const id = String(videoId || "").trim();
  if (src.includes("maxresdefault") && isValidYoutubeVideoId(id)) {
    return youtubeThumbnailUrl(id, "sddefault");
  }
  if (src.includes("sddefault") && isValidYoutubeVideoId(id)) {
    return youtubeThumbnailUrl(id, "hqdefault");
  }
  return "";
}
